import React, { useState, useEffect } from "react";
import { Phone, MapPin, Clock, Utensils, Navigation } from "lucide-react";
import { RESTAURANT } from "../mock";

// Computes open/closed status in the restaurant's local time (Jacksonville, ET).
// Hours: Closed Mondays, Tue–Sat 11 AM–8 PM, Sun 11 AM–6 PM.
function getOpenStatus() {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      hour12: false,
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).formatToParts(new Date());
    const p = Object.fromEntries(parts.map((x) => [x.type, x.value]));
    const dayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    const day = dayMap[p.weekday];
    let hour = parseInt(p.hour, 10);
    if (hour === 24) hour = 0;
    const cur = hour * 60 + parseInt(p.minute, 10);
    if (day === 1) return { open: false, label: "Closed Mondays" }; // Monday closed
    const open = 11 * 60;
    const close = day === 0 ? 18 * 60 : 20 * 60; // Sun closes 6 PM, Tue–Sat 8 PM
    if (cur >= open && cur < close) return { open: true, label: "Open now" };
    return { open: false, label: "Closed · Opens 11 AM" };
  } catch (e) {
    return { open: true, label: "Open now" };
  }
}

function TicketRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3 py-4 border-b border-dashed border-[#DCCFB4] last:border-0">
      <Icon size={18} className="text-[#D5521B] mt-1 shrink-0" />
      <div>
        <p className="label-mono text-[#8A7C68]">{label}</p>
        <div className="font-display text-lg text-[#16110E] mt-0.5">{children}</div>
      </div>
    </div>
  );
}

export default function Contact() {
  const [status, setStatus] = useState(getOpenStatus());

  useEffect(() => {
    const id = setInterval(() => setStatus(getOpenStatus()), 60000);
    return () => clearInterval(id);
  }, []);

  return (
    <section id="contact" className="py-24 md:py-32 grain-bg">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="reveal">
            <span className="label-mono text-[#D5521B]">come sit a spell</span>
            <h2 className="font-display font-black text-5xl md:text-7xl text-[#16110E] mt-4">
              Contact Us.
            </h2>
            <p className="mt-5 text-[#5E5548] leading-relaxed max-w-md">
              Call ahead, swing by, or grab pickup &amp; delivery. Y'all always
              welcome.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={RESTAURANT.phoneHref}
                className="btn-primary inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold"
              >
                <Phone size={16} /> Call to Order
              </a>
              <a
                href={RESTAURANT.directions}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold"
              >
                <Navigation size={16} /> Get Directions
              </a>
            </div>
          </div>

          {/* Guest ticket */}
          <div className="reveal">
            <div className="bg-[#FBF6EA] rounded-[22px] ticket-edge p-7 md:p-9 shadow-[0_30px_60px_-30px_rgba(60,40,20,0.5)]">
              <div className="flex items-center justify-between border-b-2 border-[#16110E] pb-4">
                <span className="font-display font-black text-xl text-[#16110E]">
                  Uncle Willie's · Guest Ticket
                </span>
                <span
                  className={`label-mono flex items-center gap-1.5 ${
                    status.open ? "text-[#1387B5]" : "text-[#D5521B]"
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full ${
                      status.open ? "bg-[#1387B5]" : "bg-[#D5521B]"
                    }`}
                  />
                  {status.label}
                </span>
              </div>

              <TicketRow icon={MapPin} label="Find Us">
                <a
                  href={RESTAURANT.directions}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-underline text-[#D5521B]"
                >
                  {RESTAURANT.address}
                  <br />
                  <span className="text-base text-[#5E5548]">
                    {RESTAURANT.city}
                  </span>
                </a>
              </TicketRow>
              <TicketRow icon={Phone} label="Call Us">
                <a
                  href={RESTAURANT.phoneHref}
                  className="link-underline text-[#D5521B]"
                >
                  {RESTAURANT.phone}
                </a>
              </TicketRow>
              <TicketRow icon={Clock} label="Hours">
                {RESTAURANT.hours}
              </TicketRow>
              <TicketRow icon={Utensils} label="How">
                {RESTAURANT.service}
              </TicketRow>

              <div className="flex items-center justify-between border-t-2 border-dashed border-[#DCCFB4] mt-4 pt-4">
                <span className="label-mono text-[#8A7C68]">Guest #7042</span>
                <span className="label-mono text-[#E0971A]">
                  Thank you kindly ★
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
